import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, message, Popconfirm, Upload, Space, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, FilePdfOutlined, DownloadOutlined, MinusCircleOutlined } from '@ant-design/icons';
import api from '../api';
import { imagepath } from '../api';
import { Card } from 'antd';

const Departments = () => {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchDepartments = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const res = await api.get('/departments', { headers: { Authorization: `Bearer ${token}` } });
    setLoading(false);
    if (res.success) {
      setDepartments(res.data);
    } else {
      message.error(res.message || 'Failed to fetch departments');
    }
  };

  useEffect(() => {
    fetchDepartments();
  }, []);

  const openModal = (record = null) => {
    setEditing(record);
    setModalVisible(true);
    if (record) {
      form.setFieldsValue({
        name: record.name,
        chamber_commerce_cert: [],
        trade_license: [],
        establishment_card: [],
        service_agency_contract: [],
        tenancy_contract: [],
        sponsor_id: [],
        sponsor_id_front: [],
        sponsor_id_back: [],
        sponsor_visa: [],
        vat: [],
        documents: record.documents?.map(doc => ({
          ...doc,
          document_file: doc.document_file ? [{
            uid: doc.id,
            name: doc.original_filename || doc.document_file.split('/').pop(),
            status: 'done',
            url: `${imagepath}${doc.document_file}`,
          }] : []
        })) || []
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        chamber_commerce_cert: [],
        trade_license: [],
        establishment_card: [],
        service_agency_contract: [],
        tenancy_contract: [],
        sponsor_id: [],
        sponsor_id_front: [],
        sponsor_id_back: [],
        sponsor_visa: [],
        vat: [],
        documents: []
      });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      const formData = new FormData();
      formData.append('name', values.name);

      const fileFields = [
        'chamber_commerce_cert',
        'trade_license',
        'establishment_card',
        'service_agency_contract',
        'tenancy_contract',
        'sponsor_id',
        'sponsor_id_front',
        'sponsor_id_back',
        'sponsor_visa',
        'vat'
      ];

      fileFields.forEach(field => {
        if (values[field] && values[field][0] && values[field][0].originFileObj) {
          formData.append(field, values[field][0].originFileObj);
        }
      });

      let res;
      if (editing) {
        // Use POST with _method=PUT for FormData compatibility with Laravel PUT
        res = await api.post(`/departments/${editing.id}?_method=PUT`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        res = await api.post('/departments', formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }
      if (res.success) {
        const departmentId = editing ? editing.id : res.data.id;

        // Handle bulk upload for insurance documents
        if (values.documents && values.documents.length > 0) {
          const docsToUpload = values.documents
            .filter(doc => doc.document_file && doc.document_file.length > 0 && doc.document_file[0].originFileObj)
            .map(doc => ({
              document_type: doc.document_type,
              expiry_date: doc.expiry_date,
              document_file: doc.document_file[0].originFileObj
            }));

          if (docsToUpload.length > 0) {
            const bulkFormData = new FormData();
            docsToUpload.forEach((doc, index) => {
              bulkFormData.append(`documents[${index}][document_type]`, doc.document_type);
              if (doc.expiry_date) bulkFormData.append(`documents[${index}][expiry_date]`, doc.expiry_date);
              bulkFormData.append(`documents[${index}][document_file]`, doc.document_file);
            });

            await api.post(`/departments/${departmentId}/documents/bulk`, bulkFormData, {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
              }
            });
          }
        }

        message.success(editing ? 'Department updated' : 'Department created');
        setModalVisible(false);
        fetchDepartments();
      } else {
        message.error(res.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  const handleRemoveFile = async (field) => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.post(`/departments/${editing.id}?_method=PUT`, { name: editing.name, [field]: 'REMOVE' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.success) {
        message.success('File removed');
        const updatedEditing = { ...editing, [field]: null };
        setEditing(updatedEditing);
        fetchDepartments();
      } else {
        message.error(res.message || 'Removal failed');
      }
    } catch (err) {
      message.error('Error removing file');
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('token');
    const res = await api.delete(`/departments/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.success) {
      message.success('Department deleted');
      fetchDepartments();
    } else {
      message.error(res.message || 'Delete failed');
    }
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 80,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Documents',
      key: 'documents',
      render: (_, record) => {
        const docs = [
          { key: 'chamber_commerce_cert', label: 'Chamber Cert' },
          { key: 'trade_license', label: 'Trade License' },
          { key: 'establishment_card', label: 'Est. Card' },
          { key: 'service_agency_contract', label: 'Agency Contract' },
          { key: 'tenancy_contract', label: 'Tenancy' },
          { key: 'sponsor_id', label: 'Sponsor ID' },
          { key: 'sponsor_id_front', label: 'Sponsor ID (Front)' },
          { key: 'sponsor_id_back', label: 'Sponsor ID (Back)' },
          { key: 'sponsor_visa', label: 'Sponsor Visa' },
          { key: 'vat', label: 'VAT' },
        ];

        const insuranceDocs = record.documents?.filter(d => d.document_type === 'employee_insurance') || [];

        return (
          <Space size={[0, 4]} wrap>
            {docs.map(doc => record[doc.key] ? (
              <Tag
                key={doc.key}
                color="blue"
                icon={<FilePdfOutlined />}
                style={{ cursor: 'pointer' }}
                onClick={() => window.open(`${imagepath}${record[doc.key]}`, '_blank')}
              >
                {doc.label}
              </Tag>
            ) : null)}
            {insuranceDocs.map((doc, idx) => (
              <Tag
                key={`insurance-${doc.id}`}
                color="green"
                icon={<FilePdfOutlined />}
                style={{ cursor: 'pointer' }}
                onClick={() => window.open(`${imagepath}${doc.document_file}`, '_blank')}
              >
                {`Insurance ${idx + 1}`}
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <>
          <Button icon={<EditOutlined />} onClick={() => openModal(record)} style={{ marginRight: 8 }} />
          <Popconfirm title="Are you sure to delete this department?" onConfirm={() => handleDelete(record.id)} okText="Yes" cancelText="No">
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2>Company</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>Add Company</Button>
      </div>
      <Table rowKey="id" columns={columns} dataSource={departments} loading={loading} bordered />
      <Modal
        title={editing ? 'Edit Department' : 'Add Department'}
        open={modalVisible}
        onOk={handleOk}
        onCancel={() => setModalVisible(false)}
        okText={editing ? 'Update' : 'Create'}
        width={700}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Company Name" rules={[{ required: true, message: 'Please enter department name' }]}>
            <Input />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {[
              { name: 'chamber_commerce_cert', label: 'Chamber of Commerce Certificate' },
              { name: 'trade_license', label: 'Trade License' },
              { name: 'establishment_card', label: 'Establishment Card' },
              { name: 'service_agency_contract', label: 'Service Agency Contract' },
              { name: 'tenancy_contract', label: 'Tenancy Contract' },
              { name: 'sponsor_id_front', label: 'Sponsor ID (Front)' },
              { name: 'sponsor_id_back', label: 'Sponsor ID (Back)' },
              { name: 'sponsor_visa', label: 'Sponsor Visa' },
              { name: 'vat', label: 'VAT' },
            ].map(doc => (
              <div key={doc.name}>
                <Form.Item
                  name={doc.name}
                  label={doc.label}
                  valuePropName="fileList"
                  getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)}
                  extra={editing && editing[doc.name] ? (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Tag color="green" icon={<FilePdfOutlined />} style={{ cursor: 'pointer', margin: 0 }} onClick={() => window.open(`${imagepath}${editing[doc.name]}`, '_blank')}>
                        View Current
                      </Tag>
                      <Popconfirm title="Remove this file?" onConfirm={() => handleRemoveFile(doc.name)}>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </div>
                  ) : null}
                >
                  <Upload
                    beforeUpload={() => false}
                    maxCount={1}
                    accept=".pdf,.jpg,.jpeg,.png"
                  >
                    <Button icon={<UploadOutlined />}>Select File</Button>
                  </Upload>
                </Form.Item>
              </div>
            ))}
          </div>

          <Card title="Employee Insurance Documents" size="small" style={{ marginTop: 24 }}>
            <Form.List name="documents">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'document_type']}
                        initialValue="employee_insurance"
                        hidden
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'document_file']}
                        label="File"
                        valuePropName="fileList"
                        getValueFromEvent={e => (Array.isArray(e) ? e : e && e.fileList)}
                        rules={[{ required: true, message: 'File is required' }]}
                      >
                        <Upload beforeUpload={() => false} maxCount={1}>
                          <Button icon={<UploadOutlined />}>Select File</Button>
                        </Upload>
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'expiry_date']}
                        label="Expiry Date"
                        getValueProps={value => ({ value: value ? value.split('T')[0] : '' })}
                      >
                        <Input type="date" style={{ width: 150 }} />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => {
                          const doc = form.getFieldValue(['documents', name]);
                          if (doc && doc.id) {
                            api.delete(`/departments/${editing.id}/documents/${doc.id}`, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            }).then(res => {
                              if (res.success) {
                                message.success('Document deleted');
                                remove(name);
                              }
                            });
                          } else {
                            remove(name);
                          }
                        }}
                        style={{ color: 'red', marginTop: 32 }}
                      />
                    </Space>
                  ))}
                  <Button type="dashed" onClick={() => add({ document_type: 'employee_insurance' })} block icon={<PlusOutlined />}>
                    Add Insurance Document
                  </Button>
                </>
              )}
            </Form.List>
          </Card>
        </Form>
      </Modal>
    </div>
  );
};

export default Departments; 